import { useState } from 'react';
import HelpTooltip from '../atoms/HelpTooltip';
import BottomSheet from '../atoms/BottomSheet';
import ImageUpload from './ImageUpload';
import { tooltipContent } from '../../content/tooltipContent';
import styles from './Initializer.module.css';

// Usually second step in persona creation, after selecting 'type' - Name/Archetype/Summary
function CoreInfoStep({
  personaData,
  setPersonaData,
  onNext,
  onBack,
  options,
  onUploadSuccess,
  currentStepNumber,
  totalSteps,
}) {
  const [formData, setFormData] = useState({
    personaName: personaData.personaName || '',
    archetype: personaData.archetype || '',
    summary: personaData.summary || '',
    imageUrl: personaData.imageUrl || null,
  });
  const [errors, setErrors] = useState({});
  const [activeTooltip, setActiveTooltip] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // TODO: Break out these numeric values into variables defined at top of page
  const validate = () => {
    const newErrors = {};
    if (!formData.personaName) newErrors.personaName = 'Name is required.';
    if (formData.personaName.length > 50)
      newErrors.personaName = 'Name must be 50 characters or less.';
    if (!formData.archetype) newErrors.archetype = 'Archetype is required.';
    if (formData.archetype.length > 30)
      newErrors.archetype = 'Archetype must be 30 characters or less.';
    if (!formData.summary) newErrors.summary = 'Summary is required.';
    if (formData.summary.length > 150)
      newErrors.summary = 'Summary must be 150 characters or less.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = ({ finalImageUrl, previewUrl }) => {
    setFormData((prev) => ({ ...prev, imageUrl: finalImageUrl }));
    onUploadSuccess({ finalImageUrl, previewUrl });
  };

  const handleNext = () => {
    if (validate()) {
      setPersonaData((prevData) => ({ ...prevData, ...formData }));
      onNext();
    }
  };

  return (
    <div>
      <h3>
        Step {currentStepNumber}/{totalSteps}: Define the Core Identity
      </h3>
      <div className={styles.formGroup}>
        <label>Persona Avatar</label>
        <ImageUpload
          currentImageUrl={formData.imageUrl}
          onUploadSuccess={handleImageUpload}
        />
      </div>
      <div className={styles.formGroup}>
        <div className={styles.labelRow}>
          <label htmlFor="personaName">
            Name
            <HelpTooltip
              content={tooltipContent.personaName}
              onMobileClick={setActiveTooltip}
            />
          </label>
          <span className={styles.charCounter}>
            {formData.personaName.length} / 50
          </span>
        </div>
        <input
          className={errors.personaName ? styles.errorInput : ''}
          type="text"
          id="personaName"
          name="personaName"
          value={formData.personaName}
          onChange={handleChange}
          placeholder="e.g., John Smith"
        />
        {errors.personaName && (
          <p className="errorText">{errors.personaName}</p>
        )}
      </div>
      <div className={styles.formGroup}>
        <div className={styles.labelRow}>
          <label htmlFor="archetype">
            Archetype
            <HelpTooltip
              content={tooltipContent.archetype}
              onMobileClick={setActiveTooltip}
            />
          </label>
          <span className={styles.charCounter}>
            {formData.archetype.length} / 30
          </span>
        </div>
        <select
          className={errors.archetype ? styles.errorInput : ''}
          id="archetype"
          name="archetype"
          value={formData.archetype}
          onChange={handleChange}
        >
          <option value="">Select an archetype...</option>
          {(options.archetypes || []).map((arch) => (
            <option key={arch} value={arch}>
              {arch}
            </option>
          ))}
        </select>
        {errors.archetype && <p className="errorText">{errors.archetype}</p>}
      </div>
      <div className={styles.formGroup}>
        <div className={styles.labelRow}>
          <label htmlFor="summary">
            One-Line Summary
            <HelpTooltip
              content={tooltipContent.summary}
              onMobileClick={setActiveTooltip}
            />
          </label>
          <span className={styles.charCounter}>
            {formData.summary.length} / 150
          </span>
        </div>
        <textarea
          className={errors.summary ? styles.errorInput : ''}
          id="summary"
          name="summary"
          value={formData.summary}
          onChange={handleChange}
          rows="3"
          placeholder="A brief, powerful description of the persona's core identity."
        />
        {errors.summary && <p className="errorText">{errors.summary}</p>}
      </div>

      <div className={styles.buttonGroup}>
        <button onClick={onBack} className="action-button secondary">
          Back
        </button>
        <button onClick={handleNext} className="action-button primary">
          Next
        </button>
      </div>

      {/* Tooltip display area on mobile. */}
      <BottomSheet
        isOpen={!!activeTooltip}
        onClose={() => setActiveTooltip(null)}
      >
        {activeTooltip}
      </BottomSheet>
    </div>
  );
}

export default CoreInfoStep;
